'use client'

import { useIsMobile } from '@/components/ui/hooks/useIsMobile'
import { Button } from '@heroui/react'
import { useCallback, useEffect, useRef, useState } from 'react'

export type LocatorNavItem = {
    divId: string
    divLabel: string
}

type LocatorSectionProps = {
    locatorItems: LocatorNavItem[]
    /** Mobile: hero image must be fully above the viewport. */
    mobileHeroBannerId?: string
    /** Mobile: activate after this sentinel (place immediately after Get Call Back). */
    mobileActivateAfterId?: string
    /** Desktop: activate when this block (section-3 CTA) enters the viewport from the bottom. */
    desktopActivateAtId?: string
}

const LocatorSection = ({
    locatorItems,
    mobileHeroBannerId,
    mobileActivateAfterId,
    desktopActivateAtId,
}: LocatorSectionProps) => {
    const isMobile = useIsMobile();
    const [isSticky, setIsSticky] = useState(false)
    const [activeSection, setActiveSection] = useState<string | null>(null)
    const stickyRef = useRef(false)

    const updateActivation = useCallback(() => {
        if (isMobile) {
            const hero = mobileHeroBannerId
                ? document.getElementById(mobileHeroBannerId)
                : null
            const sentinel = mobileActivateAfterId
                ? document.getElementById(mobileActivateAfterId)
                : null

            const heroGone =
                !hero || hero.getBoundingClientRect().bottom <= 50

            const pastGetCallBack =
                !sentinel || sentinel.getBoundingClientRect().top < 0

            stickyRef.current = heroGone || pastGetCallBack
        } else {
            const cta = desktopActivateAtId
                ? document.getElementById(desktopActivateAtId)
                : null

            if (!cta) {
                stickyRef.current = false
            } else {
                const { top } = cta.getBoundingClientRect()
                const vh = window.innerHeight
                // CTA has reached the viewport (starts showing at the bottom) or scrolled past
                stickyRef.current = top <= vh
            }
        }

        setIsSticky(stickyRef.current)
    }, [
        isMobile,
        mobileHeroBannerId,
        mobileActivateAfterId,
        desktopActivateAtId,
    ])

    useEffect(() => {
        updateActivation()

        window.addEventListener('scroll', updateActivation, { passive: true })
        window.addEventListener('resize', updateActivation)

        return () => {
            window.removeEventListener('scroll', updateActivation)
            window.removeEventListener('resize', updateActivation)
        }
    }, [updateActivation])

    /**
     * Detect active section while scrolling
     */
    useEffect(() => {
        if (!isSticky || locatorItems.length === 0) return

        const offset = window.innerHeight * 0.4

        const observer = new IntersectionObserver(
            (entries) => {
                const visibleSections = entries
                    .filter((entry) => entry.isIntersecting)
                    .sort(
                        (a, b) =>
                            a.boundingClientRect.top -
                            b.boundingClientRect.top
                    )

                if (visibleSections.length > 0) {
                    setActiveSection(visibleSections[0].target.id)
                }
            },
            {
                root: null,
                rootMargin: `-${offset}px 0px -55% 0px`,
                threshold: 0,
            }
        )

        locatorItems.forEach((item) => {
            const el = document.getElementById(item.divId)
            if (el) observer.observe(el)
        })

        return () => observer.disconnect()
    }, [isSticky, locatorItems])

    const scrollToSection = (id: string) => {
        const el = document.getElementById(id)
        if (!el) return

        const elementTop = el.getBoundingClientRect().top + window.scrollY
        const offset = window.innerHeight * 0.4

        window.scrollTo({
            top: elementTop - offset,
            behavior: 'smooth',
        })
    }

    if (locatorItems.length === 0) return null

    return (
        <>
            {isSticky ? (
                <div className="space_from_side sticky top-13 lg:top-11 xl:top-16 z-23 bg-white border-b border-gray-200">
                    <div className="grid grid-cols-4 gap-2 py-4 px-2 lg:flex lg:flex-wrap lg:justify-start lg:gap-4 lg:overflow-x-auto">
                        {locatorItems.map((curr) => {
                            const isActive = activeSection === curr.divId

                            return (
                                <Button
                                    key={curr.divId}
                                    onPress={() => scrollToSection(curr.divId)}
                                    className={`
                                        rounded-lg
                                        border
                                        w-full min-w-0 h-auto min-h-10 px-2 py-2
                                        text-center text-xs leading-tight whitespace-normal
                                        md:min-w-max md:w-auto md:px-4 md:text-sm md:leading-normal
                                        font-bold
                                        transition-all
                                        duration-300
                                        ${isActive
                                            ? 'bg-silverBlue text-white border-silverBlue'
                                            : 'bg-white border-silverBlue text-textf1'
                                        }
                                    `}
                                >
                                    {curr.divLabel}
                                </Button>
                            )
                        })}
                    </div>
                </div>
            ) : null}
        </>
    )
}

export default LocatorSection
